import { useState, useEffect } from 'react';
import { Form, Button, Alert, Spinner } from 'react-bootstrap';
import { FaClock, FaCalendarAlt, FaSave, FaTimes } from 'react-icons/fa';
import partidoService from '../../services/partidoService';
import { toast } from 'react-toastify';

/**
 * Componente para editar la hora de un partido
 * Incluye validación y notificaciones automáticas
 */
const EditarHoraPartido = ({ 
  show, 
  onHide, 
  partido, 
  onPartidoUpdated 
}) => {
  const [hora, setHora] = useState('');
  const [fecha, setFecha] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Inicializar hora y fecha cuando se abre el modal
  useEffect(() => {
    if (show && partido) {
      setHora(partido.hora || '');
      // Si el partido tiene fecha propia la usamos; si no, la de la jornada como sugerencia
      setFecha(partido.fecha || partido.jornada_fecha || '');
      setError('');
    }
  }, [show, partido]);

  // Validar formato de hora
  const validarHora = (horaValue) => {
    if (!horaValue) {
      return 'La hora es obligatoria';
    }
    
    // Aceptar formatos HH:MM y HH:MM:SS
    const horaRegex = /^([01]?[0-9]|2[0-3]):([0-5][0-9])(:([0-5][0-9]))?$/;
    if (!horaRegex.test(horaValue)) {
      return 'Formato de hora inválido. Use HH:MM o HH:MM:SS';
    }
    
    return '';
  };

  // Manejar cambio de hora
  const handleHoraChange = (e) => {
    const nuevaHora = e.target.value;
    setHora(nuevaHora);
    
    // Validar en tiempo real
    const errorValidacion = validarHora(nuevaHora);
    setError(errorValidacion);
  };

  // Guardar cambios
  const handleGuardar = async () => {
    try {
      setLoading(true);
      setError('');

      // Validar antes de enviar
      const errorValidacion = validarHora(hora);
      if (errorValidacion) {
        setError(errorValidacion);
        return;
      }

      // Actualizar hora (y fecha si aplica) del partido
      const response = await partidoService.updateHoraPartido(partido.partido_id, {
        hora: hora,
        fecha: fecha || null
      });

      if (response.success) {
        toast.success('✅ Hora del partido establecida. Se enviarán notificaciones a los participantes.');

        // Notificar al componente padre
        if (onPartidoUpdated) {
          onPartidoUpdated({
            ...partido,
            hora: hora,
            fecha: fecha || null
          });
        }

        onHide();
      } else {
        throw new Error(response.message || 'Error al establecer la hora');
      }

    } catch (error) {
      console.error('Error al actualizar hora del partido:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error desconocido';
      setError(`Error al actualizar la hora: ${errorMessage}`);
      toast.error(`Error al actualizar la hora: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  // Limpiar estado al cerrar
  const handleClose = () => {
    setHora('');
    setFecha('');
    setError('');
    setLoading(false);
    onHide();
  };

  if (!show || !partido) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1050,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: window.innerWidth <= 768 ? '0.5rem' : '1rem'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          width: '100%',
          maxWidth: window.innerWidth <= 768 ? '95%' : '500px',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header del Modal */}
        <div
          style={{
            padding: '1rem 1.5rem',
            borderBottom: '1px solid #dee2e6',
            backgroundColor: '#f8f9fa',
            position: 'relative'
          }}
        >
          <h2
            style={{
              fontSize: window.innerWidth <= 768 ? '18px' : '20px',
              fontWeight: '600',
              color: '#333',
              margin: 0,
              paddingRight: '2rem'
            }}>
            <FaClock style={{ marginRight: '0.5rem', color: '#007bff' }} />
            Editar Hora del Partido
          </h2>
          <button
            onClick={handleClose}
            style={{
              position: 'absolute',
              right: '1rem',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#6c757d',
              padding: '0',
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>

        <Form onSubmit={(e) => { e.preventDefault(); handleGuardar(); }} style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          {/* Body del Modal */}
          <div
            style={{
              padding: '1.5rem',
              backgroundColor: '#fff',
              flex: 1,
              overflowY: 'auto'
            }}
          >
          {error && (
            <Alert variant="danger" className="mb-3">
              <strong>Error:</strong> {error}
            </Alert>
          )}

          {/* Información del partido */}
          <div className="mb-4 p-3 bg-light rounded">
            <h6 className="mb-2 text-primary">📋 Información del Partido</h6>
            <div className="small text-muted">
              <div><strong>🆔 ID:</strong> {partido.partido_id}</div>
              <div><strong>⚽ Modalidad:</strong> F{partido.modalidad === 1 ? '8' : '11'}</div>
              <div><strong>📊 Estado:</strong> {
                partido.estado === 'preorganizado' ? 'Preorganizado' :
                partido.estado === 'confirmado' ? 'Cerrado' : partido.estado
              }</div>
              <div><strong>🕐 Hora actual:</strong> {partido.hora || 'Sin hora asignada'}</div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {/* Campo Hora */}
            <Form.Group>
              <Form.Label style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                <FaClock style={{ marginRight: '0.5rem', color: '#007bff' }} />
                Nueva Hora del Partido
              </Form.Label>
              <Form.Control
                type="time"
                value={hora}
                onChange={handleHoraChange}
                isInvalid={!!error}
                disabled={loading}
                step="1"
                style={{
                  fontSize: window.innerWidth <= 768 ? '16px' : '14px',
                  minHeight: window.innerWidth <= 768 ? '44px' : 'auto'
                }}
              />
              <Form.Control.Feedback type="invalid">
                {error}
              </Form.Control.Feedback>
              <Form.Text className="text-muted">
                Formato: HH:MM (ejemplo: 10:30)
              </Form.Text>
            </Form.Group>

            {/* Campo Fecha (para jornadas de 2 días) */}
            <Form.Group>
              <Form.Label style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                <FaCalendarAlt style={{ marginRight: '0.5rem', color: '#28a745' }} />
                Día del partido
              </Form.Label>
              <Form.Control
                type="date"
                value={fecha}
                onChange={e => setFecha(e.target.value)}
                disabled={loading}
                style={{
                  fontSize: window.innerWidth <= 768 ? '16px' : '14px',
                  minHeight: window.innerWidth <= 768 ? '44px' : 'auto'
                }}
              />
              <Form.Text className="text-muted">
                Para sedes de <strong>un solo día</strong> deja la fecha de la jornada. Para sedes de <strong>fin de semana</strong> (ej: Alicante) elige el día concreto (sábado o domingo).
              </Form.Text>
            </Form.Group>

            {/* Información importante */}
            <Alert variant="info" className="mb-0">
              <strong>📱 Notificaciones:</strong> Al cambiar la hora se enviarán notificaciones automáticas
              a todos los tutores y futbolistas inscritos en este partido.
            </Alert>
          </div>
        </div>

        {/* Footer del Modal */}
        <div
          style={{
            padding: '1rem 1.5rem',
            borderTop: '1px solid #dee2e6',
            backgroundColor: '#f8f9fa',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '0.5rem',
            minHeight: '80px',
            alignItems: 'center'
          }}
        >
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
            style={{
              minWidth: '80px',
              fontSize: '14px',
              padding: '0.5rem 1rem',
              minHeight: window.innerWidth <= 768 ? '44px' : 'auto'
            }}
          >
            <FaTimes className="me-2" />
            Cancelar
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={loading || !!error || !hora}
            style={{
              minWidth: '120px',
              fontSize: '14px',
              padding: '0.5rem 1rem',
              minHeight: window.innerWidth <= 768 ? '44px' : 'auto',
              display: 'block !important',
              visibility: 'visible !important',
              opacity: '1 !important',
              backgroundColor: '#007bff',
              borderColor: '#007bff',
              color: 'white'
            }}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Guardando...
              </>
            ) : (
              <>
                <FaSave className="me-2" />
                GUARDAR CAMBIOS
              </>
            )}
          </Button>
        </div>
        </Form>
      </div>
    </div>
  );
};

export default EditarHoraPartido;
